import type React from 'react';

import { Avatar } from '@change/ds-core-ui';

import type { PetitionContentUpdate } from '#src/app/pages/petitionDetails/shared/types';
import { useLoadImage } from '#src/app/shared/hooks/image';

type Props = Readonly<{
	update: PetitionContentUpdate;
}>;

const MAX_ATTEMPTS = 10;
const POLLING_TIME = 3 * 1000; // 3sec
const IMAGE_WIDTH = 40;

type MediaInfo = Readonly<{
	photo: { url: string; processing: boolean } | null;
	displayName: string | null;
	color: string;
	defaultInitial: string | null;
}>;

// oxlint-disable-next-line complexity
function getMediaInfo(update: PetitionContentUpdate): MediaInfo {
	if (update.type === 'dmResponse') {
		return {
			photo: update.photo ?? null,
			displayName: update.author?.displayName ?? null,
			color: 'bg-fill-accent-primary-strong',
			defaultInitial: 'D',
		};
	}
	if (update.type === 'starter') {
		return {
			photo: update.author?.photo ?? null,
			displayName: update.author?.displayName ?? null,
			color: 'bg-green-600',
			defaultInitial: 'S',
		};
	}
	if (update.type === 'staff') {
		return {
			photo: null,
			displayName: null,
			color: 'bg-stroke-brand-strong',
			defaultInitial: 'C',
		};
	}
	// featuredNews
	return {
		photo: update.photo,
		displayName: null,
		color: 'bg-fill-accent-primary-strong',
		defaultInitial: null,
	};
}

export const UpdateMedia: React.FC<Props> = ({ update }) => {
	const { photo, displayName, color, defaultInitial } = getMediaInfo(update);

	const imageLoaded = useLoadImage(photo?.url, {
		maxAttempts: MAX_ATTEMPTS,
		pollingTime: POLLING_TIME,
		processing: photo?.processing,
	});

	if (photo && imageLoaded) {
		return <Avatar className="min-w-10" size={IMAGE_WIDTH} src={photo.url} alt="" />;
	}

	const initial = displayName?.charAt(0) || defaultInitial || undefined;

	return (
		<Avatar
			className={color}
			size={IMAGE_WIDTH}
			initial={initial}
			{...(initial ? { initialVariant: 'strong' } : {})}
			alt=""
		/>
	);
};
